import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Barcode from 'react-barcode';
import { useBridgeClubAuth } from '../../contexts/BridgeClubAuthContext';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './BridgeClubCard.css';

interface CardData {
  member_code: string;
  barcode_id: string;
  member_name: string;
  profile_picture: string | null;
  member_since: string | null;
  qr_url: string;
  loyalty_progress: number;
  bags_until_reward: number;
  reward_available: boolean;
  available_rewards: number;
  linked_store: {
    id: number;
    name: string;
    city: string;
    state: string;
    address: string;
    zip: string;
    logo_url: string | null;
    logo_background_color: string;
  } | null;
}

type CodeView = 'qr' | 'barcode';

const BridgeClubCard: React.FC = () => {
  const { member, isAuthenticated, isLoading, refreshMember } = useBridgeClubAuth();
  const navigate = useNavigate();
  const [cardData, setCardData] = useState<CardData | null>(null);
  const [cardLoading, setCardLoading] = useState(true);
  const [codeView, setCodeView] = useState<CodeView>('qr');

  // Touch handling for swipe
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const minSwipeDistance = 50;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const distance = touchStartX.current - touchEndX.current;
    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0) {
        // Swiped left - go to barcode
        setCodeView('barcode');
      } else {
        // Swiped right - go to QR
        setCodeView('qr');
      }
    }
  }, []);

  const toggleCodeView = () => {
    setCodeView(prev => prev === 'qr' ? 'barcode' : 'qr');
  };

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/bridge-club/login');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Fetch card data with store logo info
  useEffect(() => {
    const fetchCardData = async () => {
      if (!isAuthenticated) return;
      try {
        const data = await bridgeClubAPI.getCard();
        setCardData(data);
      } catch (error) {
        console.error('Failed to load card data:', error);
      } finally {
        setCardLoading(false);
      }
    };
    fetchCardData();
  }, [isAuthenticated]);

  // Refresh member data if member_code is missing
  useEffect(() => {
    if (isAuthenticated && member && !member.member_code) {
      refreshMember();
    }
  }, [isAuthenticated, member, refreshMember]);

  const handleClose = () => {
    navigate('/bridge-club/dashboard');
  };

  if (isLoading || cardLoading) {
    return (
      <div className="fullscreen-card">
        <div className="fullscreen-card-loading">
          <div className="bridge-club-spinner"></div>
          <p>Loading your card...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return null;
  }

  const memberCode = cardData?.member_code || member.member_code || 'NO-CODE';
  // Use barcode_id if available (and not empty), otherwise fall back to member_code
  const rawBarcodeId = cardData?.barcode_id || (member as any).barcode_id;
  const barcodeId = (rawBarcodeId && rawBarcodeId.trim() !== '') ? rawBarcodeId : memberCode;
  const qrValue = cardData?.qr_url || `https://app.redbridgefeed.com/bridge-club/checkin?code=${memberCode}`;
  const linkedStore = cardData?.linked_store || member.linked_store;
  const loyaltyProgress = cardData?.loyalty_progress || member.loyalty_progress || 0;
  const bagsUntilReward = cardData?.bags_until_reward || member.bags_until_reward || 10;
  const rewardAvailable = cardData?.reward_available || member.loyalty_reward_available;
  const availableRewards = cardData?.available_rewards || (member as any).available_rewards || 0;

  // Format member since date
  const formatMemberSince = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const memberSince = formatMemberSince(cardData?.member_since || member.created_at);

  return (
    <div className="fullscreen-card">
      {/* Close Button */}
      <button className="fullscreen-close-btn" onClick={handleClose} aria-label="Close card">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>

      <div className="fullscreen-card-content">
        {/* Co-branded Logos */}
        {linkedStore?.logo_url ? (
          <div className="fullscreen-logos-container">
            <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="fullscreen-logo-cobrand" />
            <span className="fullscreen-logos-separator">+</span>
            <div
              className="fullscreen-store-logo-container"
              style={{ backgroundColor: linkedStore.logo_background_color || '#FFFFFF' }}
            >
              <img
                src={linkedStore.logo_url}
                alt={linkedStore.name}
                className="fullscreen-store-logo"
              />
            </div>
          </div>
        ) : (
          <img src="/rbf-logo.png" alt="Red Bridge Farm Feed Co." className="fullscreen-logo" />
        )}

        {/* Bridge Club Badge */}
        <div className="fullscreen-badge">BRIDGE CLUB</div>

        {/* Swipeable Code Container */}
        <div
          className="fullscreen-code-swipeable"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* QR Code View */}
          {codeView === 'qr' && (
            <div className="fullscreen-qr-container" onClick={toggleCodeView}>
              {memberCode && memberCode !== 'NO-CODE' ? (
                <QRCodeSVG
                  value={qrValue}
                  size={200}
                  level="H"
                  includeMargin={true}
                  className="fullscreen-qr"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              ) : (
                <div className="qr-placeholder">
                  <p>Loading QR Code...</p>
                </div>
              )}
            </div>
          )}

          {/* Barcode View */}
          {codeView === 'barcode' && (
            <div className="fullscreen-barcode-container" onClick={toggleCodeView}>
              <Barcode
                value={barcodeId}
                format="CODE128"
                width={2}
                height={70}
                displayValue={true}
                fontSize={14}
                margin={8}
                background="#FFFFFF"
              />
            </div>
          )}

          {/* View Toggle Dots - Now below the code */}
          <div className="code-view-dots">
            <button
              className={`code-dot ${codeView === 'qr' ? 'active' : ''}`}
              onClick={() => setCodeView('qr')}
              aria-label="QR Code"
            />
            <button
              className={`code-dot ${codeView === 'barcode' ? 'active' : ''}`}
              onClick={() => setCodeView('barcode')}
              aria-label="Barcode"
            />
          </div>

          {/* Code type label */}
          <div className="code-type-label">
            {codeView === 'qr' ? 'QR Code' : 'Barcode'} • Tap to switch
          </div>
        </div>

        {/* Member Profile Picture */}
        <div className="fullscreen-member-photo">
          {cardData?.profile_picture ? (
            <img
              src={cardData.profile_picture}
              alt={cardData.member_name || member.full_name}
              className="fullscreen-profile-pic"
            />
          ) : (
            <div className="fullscreen-profile-initials">
              {(cardData?.member_name || member.full_name || '')
                .split(' ')
                .map(n => n.charAt(0))
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </div>
          )}
        </div>

        {/* Member Name */}
        <div className="fullscreen-member-name">
          {cardData?.member_name || member.full_name}
        </div>

        {/* Member Since */}
        {memberSince && (
          <div className="fullscreen-member-since">
            Member since {memberSince}
          </div>
        )}

        {/* Member Code */}
        <div className="fullscreen-member-code">
          {memberCode}
        </div>

        {/* Available Rewards Banner */}
        {availableRewards > 0 && (
          <div className="fullscreen-rewards-banner">
            <span className="rewards-emoji">🎁</span>
            <span className="rewards-big-count">{availableRewards}</span>
            <span className="rewards-big-label">
              FREE {availableRewards === 1 ? 'BAG' : 'BAGS'} AVAILABLE!
            </span>
            <span className="rewards-redeem-hint">Show this card to redeem</span>
          </div>
        )}

        {/* Scan Instructions */}
        <div className="fullscreen-instructions">
          Show this QR code to the cashier
        </div>

        {/* Loyalty Progress - Buy 10 bags, get 11th FREE! */}
        <div className="fullscreen-loyalty">
          <div className="fullscreen-loyalty-header">Buy 10 bags, get 11th FREE!</div>
          <div className="fullscreen-loyalty-dots">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className={`fullscreen-loyalty-dot ${i < loyaltyProgress ? 'filled' : ''}`}
              />
            ))}
          </div>
          <div className="fullscreen-loyalty-text">
            {bagsUntilReward === 10
              ? 'Starting fresh! Buy 10 bags for your 11th FREE!'
              : `${bagsUntilReward} more bag${bagsUntilReward !== 1 ? 's' : ''} to your FREE bag!`}
          </div>
        </div>

        {/* Home Store */}
        {linkedStore && (
          <div className="fullscreen-store">
            {linkedStore.name} • {linkedStore.city}, {linkedStore.state}
          </div>
        )}
      </div>
    </div>
  );
};

export default BridgeClubCard;
